/**
 * usePublishProgress.ts — PLAT-10707, accuracy pass PLAT-11209
 *
 * Smooth estimated progress for the publish drawer. The render pipeline
 * exposes no percentage (Creatomate reports only discrete statuses, and the
 * backend writes just `rendering` → `succeeded`/`failed` to Dato), so this
 * mirrors the `useProcessingProgress` idiom: per-variant milestone floors +
 * exponential-decay time interpolation between the real status pushes,
 * aggregated as a mean, ratcheted monotonic, and hard-capped below 100 until
 * the real terminal push lands.
 *
 * Two things make the estimate track reality rather than a fixed guess:
 *
 *  - The ETA is per-variant and keyed off **that clip's** output duration. The
 *    original version shared one ETA computed from the story's source video, so
 *    a 40s clip cut from a 30-minute interview got a ~61-minute ETA and the bar
 *    crawled to ~8% before snapping to 100%.
 *  - The bands are contiguous (3 → 5…20 → 20…95 → 100), so the `queued` creep
 *    hands off to the rendering curve without the ratchet stalling the bar.
 *
 * Where the rendering band sits, as a fraction of the expected ETA: 0.25 → 60%,
 * 0.5 → 78%, 0.75 → 87%, 1.0 → 91%, 1.5+ → 94–95%. So a render that lands on
 * its ETA leaves only a ~9-point jump to 100.
 *
 * Known residual: the terminal 100% can lag reality by up to ~15s, because a
 * `succeeded` push may arrive on `useDatoQuerySubscription`'s fallback poll
 * (POLL_INTERVAL_MS = 15_000) rather than live. Closing that needs either real
 * progress signals from the render pipeline or VIDEO_RENDER_FINISHED_EVENT
 * (`useRenderStatusObserver`) wired into the drawer.
 */

import { estimatePublishRenderMs } from '@src/config/processingEstimates';
import { PublishStatus } from '@src/types.ts/publishRecord';
import { isPublishRecordFinished } from '@src/utility/publishRecord';
import { useEffect, useRef, useState } from 'react';

import type { PublishRecord } from '@src/types.ts/publishRecord';

/** "Preparing" optimistic window — Publish clicked, records not created yet. */
export const PENDING_PROGRESS = 3;
/** Floor for a record whose render hasn't started (`queued` / `retry_pending`). */
export const QUEUED_PROGRESS = 5;
/**
 * Top of the queued band, and the floor the rendering band starts from. The two
 * bands must meet: a variant that crept to QUEUED_CAP while queued would
 * otherwise stall there (the ratchet holds it) until the rendering curve climbed
 * back past it.
 */
export const QUEUED_CAP = 20;
/** Hard cap while `rendering` — only a real terminal push reaches 100. */
export const RENDER_CAP = 95;
export const TICK_MS = 500;
/**
 * Expected dispatch → `rendering` handoff. Covers the lambda's Dato write plus
 * the subscription push, so the bar creeps instead of sitting frozen at 5% —
 * which read as broken.
 */
export const QUEUE_ETA_MS = 20_000;
/**
 * Decay rate for the interpolated bands. 3 puts us at ~95% of the band at the
 * expected ETA (a ~9-point jump left to 100) while still creeping toward the cap
 * when a render runs longer than predicted. `useProcessingProgress` uses 2,
 * which left a ~15-point jump — too big now that the ETA is per-clip and
 * therefore trustworthy enough to land near the cap.
 */
export const SEGMENT_DECAY_RATE = 3;

type ProgressRecord = Pick<
  PublishRecord,
  'id' | 'videoId' | 'publishStatus' | 'renderStartedAt'
>;

/**
 * First-seen clocks, per phase and keyed by record id. Kept separate because a
 * record that waited in `queued` must not have that wait counted as render
 * time: `queuedAt` drives the queued creep, `renderingAt` only backstops a
 * `rendering` push whose `renderStartedAt` hadn't landed yet.
 */
export interface ProgressClocks {
  queuedAt: Map<string, number>;
  renderingAt: Map<string, number>;
}

/** Fraction of a band consumed after `elapsedMs` against an expected `etaMs`. */
function decayFraction(elapsedMs: number, etaMs: number): number {
  if (etaMs <= 0) return 0;
  return 1 - Math.exp((-SEGMENT_DECAY_RATE * elapsedMs) / etaMs);
}

function progressForRecord(
  record: ProgressRecord,
  now: number,
  etaMsFor: (record: ProgressRecord) => number,
  clocks: ProgressClocks,
): number {
  switch (record.publishStatus) {
    // Failed counts as complete — matches the drawer's finishedCount
    // semantics; the red "· N failed" text carries the failure.
    case PublishStatus.Succeeded:
    case PublishStatus.Failed:
      return 100;
    case PublishStatus.Rendering: {
      const parsed = record.renderStartedAt
        ? Date.parse(record.renderStartedAt)
        : NaN;
      const startMs = Number.isFinite(parsed)
        ? parsed
        : clocks.renderingAt.get(record.id);
      // Rendering with no clock yet sits at the band's floor, not the queued
      // floor — it is strictly further along than a queued variant.
      if (startMs === undefined) return QUEUED_CAP;
      const elapsedMs = Math.max(0, now - startMs);
      const fraction = decayFraction(elapsedMs, etaMsFor(record));
      return Math.min(
        QUEUED_CAP + (RENDER_CAP - QUEUED_CAP) * fraction,
        RENDER_CAP,
      );
    }
    // queued / retry_pending — creep toward QUEUED_CAP from when we first saw
    // this record, so the wait for the lambda's `rendering` write reads as
    // progress. No clock yet (first render) → the floor.
    default: {
      const startMs = clocks.queuedAt.get(record.id);
      if (startMs === undefined) return QUEUED_PROGRESS;
      const elapsedMs = Math.max(0, now - startMs);
      return Math.min(
        QUEUED_PROGRESS +
          (QUEUED_CAP - QUEUED_PROGRESS) *
            decayFraction(elapsedMs, QUEUE_ETA_MS),
        QUEUED_CAP,
      );
    }
  }
}

/**
 * Unratcheted 0–100 aggregate (mean over variants). Exported pure so the
 * math is unit-testable with a fixed `now`. `etaMsFor` is injected rather than
 * computed here, which keeps the per-clip duration lookup (a story-graph
 * concern) out of the math.
 */
export function computePublishProgress(
  records: ProgressRecord[],
  now: number,
  etaMsFor: (record: ProgressRecord) => number,
  clocks: ProgressClocks,
): number {
  if (records.length === 0) return 0;
  let sum = 0;
  for (const record of records) {
    sum += progressForRecord(record, now, etaMsFor, clocks);
  }
  return sum / records.length;
}

/**
 * Monotonic 0–100 progress for the publish drawer — drives both the bar and
 * the percentage text. Ticks (500ms) only while unfinished work exists.
 *
 * `durationByVideoId` maps a record's `videoId` to that clip's output duration
 * in seconds (`clipJson.duration`); records with no entry fall back to the
 * estimator's own clip-length default.
 */
export function usePublishProgress(
  records: PublishRecord[],
  pendingPublishCount: number | null,
  durationByVideoId: Map<string, number>,
): number {
  const [, tick] = useState(0);
  // Per-instance state — no module-level Maps, so memory and test isolation
  // come for free with the component lifecycle (useProcessingProgress idiom).
  const clocksRef = useRef<ProgressClocks>({
    queuedAt: new Map(),
    renderingAt: new Map(),
  });
  const lastPercentRef = useRef(0);
  const knownIdsRef = useRef<Set<string>>(new Set());

  const allFinished =
    records.length > 0 && records.every(isPublishRecordFinished);
  const isActive =
    pendingPublishCount !== null ||
    records.some(record => !isPublishRecordFinished(record));

  useEffect(() => {
    if (!isActive) return;
    const id = window.setInterval(() => tick(t => t + 1), TICK_MS);
    return () => window.clearInterval(id);
  }, [isActive]);

  const predictedMs = (record: ProgressRecord): number =>
    estimatePublishRenderMs({
      clipDurationSec: durationByVideoId.get(record.videoId),
    });

  // Time-capturing writes happen in commit-phase effects, NOT during render
  // (StrictMode double-renders would bias elapsed-time computations).
  useEffect(() => {
    const { queuedAt, renderingAt } = clocksRef.current;

    // Clocks for the interpolated bands, stamped once per phase on first sight.
    // The rendering one is only a backstop for a lean subscription push that
    // raced its own `renderStartedAt` field.
    for (const record of records) {
      if (record.publishStatus === PublishStatus.Rendering) {
        if (!record.renderStartedAt && !renderingAt.has(record.id)) {
          renderingAt.set(record.id, Date.now());
        }
      } else {
        // No longer rendering (retry_pending / terminal): drop the backstop
        // clock, or a retried render would time from the failed attempt's start
        // and snap the bar to RENDER_CAP.
        renderingAt.delete(record.id);
        if (!isPublishRecordFinished(record) && !queuedAt.has(record.id)) {
          queuedAt.set(record.id, Date.now());
        }
      }
    }

    // Reset the ratchet only when a NEW record id joins an existing batch (a
    // second publish was tracked into the drawer — the honest aggregate
    // legitimately drops). Replays/merges of known ids never reset it.
    const known = knownIdsRef.current;
    const hasNew = records.some(record => !known.has(record.id));
    if (hasNew && known.size > 0) {
      lastPercentRef.current = 0;
    }
    if (hasNew) {
      knownIdsRef.current = new Set(records.map(record => record.id));
    }
  });

  const ratchet = (raw: number): number => {
    const next = Math.max(lastPercentRef.current, raw);
    lastPercentRef.current = next;
    return next;
  };

  if (records.length === 0) {
    return pendingPublishCount !== null
      ? ratchet(PENDING_PROGRESS)
      : lastPercentRef.current;
  }

  if (allFinished) return ratchet(100);

  return ratchet(
    computePublishProgress(records, Date.now(), predictedMs, clocksRef.current),
  );
}
